import type { TokenBridgeConfig } from "./types";

let config: TokenBridgeConfig | null = null;
let accessToken: string | null = null;
let refreshToken: string | null = null;
let inFlight: Promise<string | null> | null = null;
let signOutHandler: () => void | Promise<void> = () => {};

type RefreshResponse = { jwt?: string; jwt_refresh_token?: string };

async function doRefresh(): Promise<string | null> {
  if (!config) return null;
  const rt = refreshToken ?? (await config.storage.getItem<string>(config.refreshTokenKey)) ?? null;
  if (!rt) return null;
  refreshToken = rt;
  try {
    const res = await fetch(config.refreshEndpoint, {
      method: "POST",
      headers: { "x-jwt-refresh-token": rt },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as RefreshResponse;
    if (!body?.jwt) return null;
    accessToken = body.jwt;
    if (body.jwt_refresh_token) {
      refreshToken = body.jwt_refresh_token;
      await config.storage.setItem(config.refreshTokenKey, body.jwt_refresh_token);
    }
    return body.jwt;
  } catch {
    return null;
  }
}

/**
 * Module-singleton bridge between the React auth layer and Apollo. Owns the
 * in-memory JWT + refresh token and the single-flight refresh. Imports
 * neither React nor Apollo.
 */
export const tokenBridge = {
  configure(next: TokenBridgeConfig): void {
    config = next;
  },
  getToken: (): string | null => accessToken,
  setToken: (token: string | null): void => {
    accessToken = token;
  },
  getRefreshToken: (): string | null => refreshToken,
  async setRefreshToken(token: string): Promise<void> {
    refreshToken = token;
    if (config) await config.storage.setItem(config.refreshTokenKey, token);
  },
  async clearRefreshToken(): Promise<void> {
    refreshToken = null;
    if (config) await config.storage.removeItem(config.refreshTokenKey);
  },
  registerHandlers(handlers: { signOut: () => void | Promise<void> }): void {
    signOutHandler = handlers.signOut;
  },
  signOut: (): void | Promise<void> => signOutHandler(),
  refresh(): Promise<string | null> {
    if (inFlight) return inFlight;
    inFlight = doRefresh().finally(() => {
      inFlight = null;
    });
    return inFlight;
  },
};
